import { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router'
import {
  IoHomeOutline,
  IoGameControllerOutline,
  IoPersonOutline,
  IoPeopleOutline,
  IoGiftOutline,
  IoLogOutOutline,
} from 'react-icons/io5'
import { GiFeather, GiMagnifyingGlass } from 'react-icons/gi'
import {
  PiDotsThreeCircle,
  PiGear,
  PiBookmarkSimple,
  PiBellRinging,
  PiEnvelopeOpenLight,
} from 'react-icons/pi'

import { Action } from '../../context/GlobalContext'
import { useScreenContext } from '../../context/GlobalContext'
import { useLogoutUserMutation } from '../../services/apiSlice'
import { CurrentUser } from '../../services/schema/User'
import SubLinksOverlay from './SubLinksOverlay'

const links = [
  {
    name: 'Logo',
    href: '/home',
    icon: null,
    mobile: null,
  },
  {
    name: 'Home',
    href: '/home',
    icon: <IoHomeOutline size={28} />,
    mobile: true,
  },
  {
    name: 'Explore',
    href: '/explore',
    icon: <GiMagnifyingGlass size={28} />,
    mobile: true,
  },
  {
    name: 'Notifications',
    href: '/notifications',
    icon: <PiBellRinging size={28} />,
    mobile: false,
  },
  {
    name: 'Messages',
    href: '/messages',
    icon: <PiEnvelopeOpenLight size={28} />,
    mobile: false,
  },
  {
    name: 'Bookmarks',
    href: '/bookmarks',
    icon: <PiBookmarkSimple size={28} />,
    mobile: false,
  },
  {
    name: 'Communities',
    href: '/communities',
    icon: <IoPeopleOutline size={28} />,
    mobile: false,
  },
  {
    name: 'Games',
    href: '/games',
    icon: <IoGameControllerOutline size={28} />,
    mobile: false,
  },
  {
    name: 'Surprise Gift',
    href: '/surprise',
    icon: <IoGiftOutline size={28} />,
    mobile: false,
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: <PiGear size={28} />,
    mobile: false,
  },
  {
    name: 'Profile',
    href: '',
    icon: <IoPersonOutline size={28} />,
    mobile: true,
  },
  {
    name: 'Logout',
    href: '',
    icon: <IoLogOutOutline size={28} />,
    mobile: true,
  },
]

interface Props {
  dispatch: React.Dispatch<Action>
  user: CurrentUser
}

const Banner: React.FC<Props> = ({ dispatch, user }) => {
  const { innerWidth } = useScreenContext()
  const location = useLocation()
  const overlayRef = useRef<HTMLDivElement>(null)
  const [overlayIsActive, setOverlayIsActive] = useState(false)
  const [logout] = useLogoutUserMutation()

  useEffect(() => {
    setOverlayIsActive(false)
  }, [location])

  if (user) {
    const profilePic =
      user.profilePicture !== null ? user.profilePicture : '/user_sample.jpg'
    if (innerWidth > 519) {
      const linksComponents = links.map((link, idx) => {
        if (idx === 0) {
          return (
            <div
              key={link.name + idx}
              className="my-[5px] flex w-full items-center justify-center text-xl xl:justify-start"
            >
              <Link to={link.href} className="hover-main rounded-full p-[10px]">
                <div className="exile-regular flex h-auto w-full items-center text-2xl">
                  Y?
                </div>
              </Link>
            </div>
          )
        }

        if (link.name === 'Profile') {
          return (
            <div
              key={link.name + idx}
              className="mb-[5px] flex w-full items-center justify-center text-xl xl:justify-start"
            >
              <Link
                to={`/${user?.username}`}
                className="item-center hover-main flex justify-center rounded-full p-[12px]"
              >
                <div className="item-center flex justify-center">
                  {link.icon}
                </div>
                <span className="hidden px-[25px] xl:flex">{link.name}</span>
              </Link>
            </div>
          )
        }

        if (link.name === 'Logout') {
          return (
            <div
              key={link.name + idx}
              className="mb-[5px] flex w-full items-center justify-center text-xl xl:justify-start"
            >
              <button
                type="button"
                className="item-center hover-main flex justify-center rounded-full p-[12px]"
                onClick={() => {
                  if (confirm('Are you sure you want to logout?')) {
                    logout(null)
                  }
                }}
              >
                <div className="item-center flex justify-center">
                  {link.icon}
                </div>
                <span className="hidden px-[25px] xl:flex">{link.name}</span>
              </button>
            </div>
          )
        }

        return (
          <div
            key={link.name + idx}
            className="mb-[5px] flex w-full items-center justify-center text-xl xl:justify-start"
          >
            <Link
              to={link.href}
              className="hover-main flex items-center justify-center rounded-full p-[12px]"
            >
              <div className="flex items-center justify-center">
                {link.icon}
              </div>
              <span className="hidden px-[25px] xl:flex">{link.name}</span>
            </Link>
          </div>
        )
      })

      return (
        <header className="relative flex grow justify-end">
          <div className="relative h-0 w-[80px] xl:w-[275px]">
            <div className="fixed top-0">
              <div className="h-dvh w-[80px] xl:w-[275px]">
                <section className="flex h-full w-full flex-col items-start">
                  {linksComponents}

                  <div className="mt-auto flex w-full flex-col justify-center pb-[15px] xl:justify-start">
                    <div className="mt-[30px] mb-[15px] flex w-full justify-center text-lg xl:justify-start">
                      <button
                        type="button"
                        onClick={() => {
                          dispatch({ type: 'OPEN_POST_OVERLAY' })
                        }}
                        className="bgd-blue cursor-pointer rounded-full"
                      >
                        <p className="hidden text-white xl:flex xl:px-[100px] xl:py-[10px]">
                          Post
                        </p>
                        <div className="item-center flex justify-center p-[10px] xl:hidden">
                          <GiFeather size={28} />
                        </div>
                      </button>
                    </div>
                    <div className="mt-auto flex justify-center pb-[15px] xl:justify-start">
                      <div className="hover-main flex w-auto cursor-pointer items-center rounded-full p-[10px] xl:p-[15px]">
                        <Link to={`/${user?.username}`} className="flex">
                          <div
                            data-id="profile-picture"
                            className="flex h-[40px] w-[40px] cursor-pointer items-center justify-center rounded-full bg-cover bg-center"
                            style={{
                              backgroundImage: `url(${profilePic})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          />
                          <div className="ml-[15px] hidden flex-col items-start text-sm xl:flex">
                            <p className="block">{user?.displayName}</p>
                            <p className="text-sub block">@{user?.username}</p>
                          </div>
                        </Link>

                        <button
                          type="button"
                          className="hover-sub align-center justify-content ml-[30px] hidden cursor-pointer text-xl xl:flex"
                          onClick={() => {
                            if (confirm('Are you sure you want to logout?')) {
                              logout(null)
                            }
                          }}
                        >
                          <IoLogOutOutline />
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </header>
      )
    } else {
      const filteredMain = links.filter((link) => link.mobile === true)
      const filteredSub = links.filter((link) => link.mobile === false)
      const linksMain = filteredMain.map((link, idx) => {
        if (link.name === 'Profile') {
          return (
            <div
              key={link.name + idx}
              className="flex w-full items-center justify-center p-[10px] text-xl"
            >
              <Link
                to={`/${user?.username}`}
                className="item-center hover-main flex justify-center rounded-full"
              >
                <div className="item-center flex justify-center">
                  {link.icon}
                </div>
              </Link>
            </div>
          )
        }

        if (link.name === 'Logout') {
          return (
            <div
              key={link.name + idx}
              className="flex w-full items-center justify-center p-[10px] text-xl"
            >
              <button
                type="button"
                className="item-center hover-main flex justify-center rounded-full"
                onClick={() => {
                  if (confirm('Are you sure you want to logout?')) {
                    logout(null)
                  }
                }}
              >
                <div className="item-center flex justify-center">
                  {link.icon}
                </div>
              </button>
            </div>
          )
        }

        return (
          <div
            key={link.name + idx}
            className="flex w-full items-center justify-center p-[10px] text-xl"
          >
            <Link
              to={link.href}
              className="item-center hover-main flex justify-center rounded-full"
            >
              <div className="item-center flex justify-center">{link.icon}</div>
            </Link>
          </div>
        )
      })

      const linksSub = filteredSub.map((link, idx) => {
        return (
          <Link
            key={link.name + idx}
            to={link.href}
            className="hover-main flex items-center rounded-full p-[12px]"
          >
            <div className="item-center flex justify-center">{link.icon}</div>
            <span className="px-[25px]">{link.name}</span>
          </Link>
        )
      })

      return (
        <>
          <div className="bgd-theme border-theme fixed bottom-[-1px] z-50 flex h-[60px] w-dvw items-center justify-evenly border-r border-b border-l p-[5px]">
            {linksMain}
            <div className="flex w-full items-center justify-center p-[10px] text-xl">
              <button
                type="button"
                className="item-center hover-main flex justify-center rounded-full"
                onClick={() => {
                  setOverlayIsActive(true)
                }}
              >
                <div className="item-center flex justify-center text-xl">
                  <PiDotsThreeCircle />
                </div>
              </button>
            </div>
          </div>
          {overlayIsActive && (
            <SubLinksOverlay
              overlayRef={overlayRef}
              setOverlayIsActive={setOverlayIsActive}
              linksSub={linksSub}
            />
          )}
        </>
      )
    }
  }
}

export default Banner
